import type React from 'react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

/** Mirrors the server policy so the operator gets feedback before submitting. */
const MIN_LENGTH = 12;

export function ChangePasswordPage(): React.JSX.Element {
  const { admin, changePassword } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit =
    current.length > 0 && next.length >= MIN_LENGTH && next === confirm && !busy;

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível trocar a senha.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={(e) => void onSubmit(e)}>
        <h1>Definir nova senha</h1>
        {admin?.mustChangePassword ? (
          <p className="auth-card__note">
            Sua senha atual foi definida por outra pessoa. Escolha uma nova para continuar.
          </p>
        ) : null}

        <label className="field">
          <span>Senha atual</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span>Nova senha (mínimo {MIN_LENGTH} caracteres)</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
          />
          {tooShort ? (
            <small className="field__hint field__hint--warn">
              Faltam {MIN_LENGTH - next.length} caractere(s).
            </small>
          ) : null}
        </label>

        <label className="field">
          <span>Confirme a nova senha</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
          {mismatch ? (
            <small className="field__hint field__hint--warn">As senhas não coincidem.</small>
          ) : null}
        </label>

        {error ? <div className="form-error">{error}</div> : null}

        <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
          {busy ? 'Salvando…' : 'Salvar senha'}
        </button>
      </form>
    </div>
  );
}
